import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  KMLConvertedData,
  KMLGroundOverlay,
  GeoJSONFeatureCollection,
  GeoJSONFeature,
} from '../models/kml-geojson.model';
import { environment } from 'src/environments/environment';

/**
 * Service to parse KML files and convert them to GeoJSON + GroundOverlay metadata
 * Handles the migration from Google's KmlLayer to MapLibre-compatible formats
 *
 * IMPORTANT: This is a client-side parser for audit/preview purposes.
 * Production implementation MUST use backend (PostGIS) conversion as per DASH-3062.
 * Backend should convert on ingest to avoid GroundOverlay data loss.
 */
@Injectable({
  providedIn: 'root',
})
export class KMLParserService {
  private readonly http: HttpClient;

  /*
   * KML assets are served from blob storage, not the API, so they are fetched over
   * HttpBackend rather than the injected HttpClient. That skips the interceptor chain
   * and, critically, TokenInterceptor — the user's Bearer JWT is never attached to a
   * request aimed at a third-party host.
   */
  constructor(handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  /**
   * Fetch and parse a KML file from URL
   * Returns both vector data (GeoJSON) and raster data (GroundOverlays)
   *
   * The origin allowlist bounds where the app will fetch from. It no longer has to
   * guard the access token (see the constructor), so a blob-storage host can be added
   * through environment.kmlTrustedOrigins instead of being blocked outright.
   */
  parseKMLFromUrl(url: string): Observable<KMLConvertedData> {
    if (!this.isTrustedOrigin(url)) {
      return throwError(() => new Error('KML URL from untrusted origin — request blocked'));
    }
    return this.http.get(url, { responseType: 'text' }).pipe(
      map((kmlString) => this.parseKMLString(kmlString, url)),
      catchError(() => {
        return throwError(() => new Error('Failed to parse KML content'));
      }),
    );
  }

  /**
   * A GroundOverlay's Icon href is often a signed CDN URL (`?Expires=...&Signature=...`), and a
   * bare `&` between query params is invalid XML — DOMParser rejects the entire document with
   * `EntityRef: expecting ';'` rather than failing just that element. Google's KmlLayer parsed
   * this server-side, tolerant of the malformed markup; escaping stray `&` here restores that
   * tolerance for the client-side parser.
   */
  private escapeBareAmpersands(kmlString: string): string {
    return kmlString.replace(/&(?!amp;|lt;|gt;|apos;|quot;|#\d+;|#x[0-9a-fA-F]+;)/g, '&amp;');
  }

  /** Resolves a possibly-relative href against the KML document's own URL, as a server-side fetcher would. */
  private resolveHref(href: string, kmlUrl: string): string | null {
    try {
      return new URL(href, kmlUrl).href;
    } catch {
      return null;
    }
  }

  /** Rejects relative URLs so they can't resolve against the trusted page origin and bypass the allowlist */
  private isTrustedOrigin(url: string): boolean {
    if (!/^https?:\/\//i.test(url)) return false;
    try {
      return this.trustedOrigins().includes(new URL(url).origin);
    } catch {
      return false;
    }
  }

  /**
   * The app's own origin and the API, plus any blob-storage / CDN hosts configured for
   * the environment. Entries that are not parseable URLs are dropped rather than
   * widening the allowlist by accident.
   */
  private trustedOrigins(): string[] {
    const configured = (environment.kmlTrustedOrigins ?? []).reduce<string[]>((origins, entry) => {
      try {
        origins.push(new URL(entry).origin);
      } catch {
        /* a malformed entry must not become a trusted origin */
      }
      return origins;
    }, []);
    return [window.location.origin, new URL(environment.baseUrl).origin, ...configured];
  }

  /**
   * Parse KML content string
   * CRITICAL: This extracts both GroundOverlays AND vector features
   */
  private parseKMLString(kmlString: string, kmlUrl: string): KMLConvertedData {
    try {
      const parser = new DOMParser();
      const kmlDoc = parser.parseFromString(this.escapeBareAmpersands(kmlString), 'application/xml');

      if (kmlDoc.getElementsByTagName('parsererror').length > 0) {
        throw new Error('Invalid KML document');
      }

      const features: GeoJSONFeature[] = [];
      const overlays: KMLGroundOverlay[] = [];
      const warnings: string[] = [];

      // Parse GroundOverlays (CRITICAL - raster floorplans)
      const groundOverlays = kmlDoc.getElementsByTagName('GroundOverlay');
      for (let i = 0; i < groundOverlays.length; i++) {
        const overlay = this.parseGroundOverlay(groundOverlays[i], warnings, kmlUrl);
        if (overlay) {
          overlays.push(overlay);
        }
      }

      // Parse vector features (Placemarks)
      const placemarks = kmlDoc.getElementsByTagName('Placemark');
      for (let i = 0; i < placemarks.length; i++) {
        const feature = this.parsePlacemark(placemarks[i]);
        if (feature) {
          features.push(feature);
        }
      }

      // Parse document metadata
      const documentName = this.getElementTextContent(
        kmlDoc.getElementsByTagName('name'),
      );
      const documentDescription = this.getElementTextContent(
        kmlDoc.getElementsByTagName('description'),
      );

      return {
        vectorData: {
          type: 'FeatureCollection',
          features,
        },
        overlays,
        metadata: {
          name: documentName,
          description: documentDescription,
        },
        warnings,
      };
    } catch (error) {
      throw new Error(`KML parsing failed: ${(error as Error).message}`);
    }
  }

  /**
   * Extract GroundOverlay (georeferenced raster) from KML
   * This is the CRITICAL part for indoor floorplans
   *
   * GroundOverlay structure:
   * - Icon/href → image URL
   * - LatLonBox → north, south, east, west coordinates
   * - rotation → optional image rotation
   * - opacity → optional transparency
   */
  private parseGroundOverlay(
    element: Element,
    warnings: string[],
    kmlUrl: string,
  ): KMLGroundOverlay | null {
    const name = this.getElementTextContent(
      element.getElementsByTagName('name'),
    );
    const description = this.getElementTextContent(
      element.getElementsByTagName('description'),
    );

    const iconElement = element.getElementsByTagName('Icon')[0];
    if (!iconElement) {
      warnings.push(`GroundOverlay missing Icon element: ${name}`);
      return null;
    }

    const href = this.getElementTextContent(
      iconElement.getElementsByTagName('href'),
    );
    if (!href) {
      warnings.push(`GroundOverlay Icon missing href: ${name}`);
      return null;
    }

    /* A GroundOverlay's Icon href is commonly authored relative to the KML document
       itself (e.g. "files/floor1.png"). Google's KmlLayer resolved this server-side;
       parsing client-side means we must resolve it against the KML's own URL before
       the origin check, or every relative-href overlay is dropped as "untrusted". */
    const imageUrl = this.resolveHref(href, kmlUrl);
    if (!imageUrl) {
      warnings.push(`GroundOverlay Icon href could not be resolved: ${name}`);
      return null;
    }
    if (!this.isTrustedOrigin(imageUrl)) {
      warnings.push(`GroundOverlay Icon href is not a trusted origin: ${name}`);
      return null;
    }

    const latLonBox = element.getElementsByTagName('LatLonBox')[0];
    if (!latLonBox) {
      warnings.push(`GroundOverlay missing LatLonBox: ${name}`);
      return null;
    }

    const north = parseFloat(
      this.getElementTextContent(latLonBox.getElementsByTagName('north')),
    );
    const south = parseFloat(
      this.getElementTextContent(latLonBox.getElementsByTagName('south')),
    );
    const east = parseFloat(
      this.getElementTextContent(latLonBox.getElementsByTagName('east')),
    );
    const west = parseFloat(
      this.getElementTextContent(latLonBox.getElementsByTagName('west')),
    );

    if (
      Number.isNaN(north) || Number.isNaN(south) || Number.isNaN(east) || Number.isNaN(west) ||
      Math.abs(north) > 90 || Math.abs(south) > 90 || Math.abs(east) > 180 || Math.abs(west) > 180 ||
      north <= south || east <= west
    ) {
      warnings.push(`GroundOverlay has invalid or out-of-range coordinates: ${name}`);
      return null;
    }

    const rawRotation = latLonBox.getElementsByTagName('rotation')[0]
      ? parseFloat(
          this.getElementTextContent(
            latLonBox.getElementsByTagName('rotation'),
          ),
        )
      : 0;
    const rotation = Number.isNaN(rawRotation) ? 0 : rawRotation;

    return {
      name,
      description,
      imageUrl,
      north,
      south,
      east,
      west,
      rotation,
      opacity: 1.0,
    };
  }

  /**
   * Parse Placemark (vector feature) from KML
   * Converts KML geometry (Point, LineString, Polygon) to GeoJSON
   */
  private parsePlacemark(element: Element): GeoJSONFeature | null {
    const name = this.getElementTextContent(element.getElementsByTagName('name'));
    const description = this.getElementTextContent(
      element.getElementsByTagName('description'),
    );

    const pointElement = element.getElementsByTagName('Point')[0];
    if (pointElement) {
      const coordinates = this.parseCoordinates(
        pointElement.getElementsByTagName('coordinates'),
      );
      if (coordinates && coordinates.length > 0) {
        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinates[0],
          },
          properties: {
            name,
            description,
          },
          id: name,
        };
      }
    }

    const lineStringElement = element.getElementsByTagName('LineString')[0];
    if (lineStringElement) {
      const coordinates = this.parseCoordinates(
        lineStringElement.getElementsByTagName('coordinates'),
      );
      if (coordinates && coordinates.length > 1) {
        return {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates as [number, number][],
          },
          properties: {
            name,
            description,
          },
          id: name,
        };
      }
    }

    const polygonElement = element.getElementsByTagName('Polygon')[0];
    if (polygonElement) {
      const outerBoundary = polygonElement.getElementsByTagName('outerBoundaryIs')[0];
      if (outerBoundary) {
        const coordinates = this.parseCoordinates(
          outerBoundary.getElementsByTagName('coordinates'),
        );
        if (coordinates && coordinates.length > 2) {
          const rings: [number, number][][] = [coordinates as [number, number][]];

          // Parse inner rings (holes)
          const innerBoundaries = polygonElement.getElementsByTagName('innerBoundaryIs');
          for (let i = 0; i < innerBoundaries.length; i++) {
            const innerCoords = this.parseCoordinates(
              innerBoundaries[i].getElementsByTagName('coordinates'),
            );
            if (innerCoords && innerCoords.length > 2) {
              rings.push(innerCoords as [number, number][]);
            }
          }

          return {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: rings,
            },
            properties: {
              name,
              description,
            },
            id: name,
          };
        }
      }
    }

    return null;
  }

  /**
   * Parse KML coordinates element
   * Format: "lon,lat[,alt] lon,lat[,alt] ..."
   */
  private parseCoordinates(elements: HTMLCollectionOf<Element>): [number, number][] {
    if (elements.length === 0) {
      return [];
    }

    const coordString = elements[0].textContent || '';
    const coordinates: [number, number][] = [];

    coordString
      .trim()
      .split(/\s+/)
      .forEach((coord) => {
        const parts = coord.split(',');
        if (parts.length >= 2) {
          const lon = parseFloat(parts[0]);
          const lat = parseFloat(parts[1]);
          if (!Number.isNaN(lon) && !Number.isNaN(lat)) {
            coordinates.push([lon, lat]);
          }
        }
      });

    return coordinates;
  }

  /**
   * Helper to get text content from element collection
   */
  private getElementTextContent(
    elements: HTMLCollectionOf<Element>,
  ): string {
    return elements.length > 0 ? (elements[0].textContent || '').trim() : '';
  }
}
